import React, { useEffect, useState } from "react";
import { BrowserStorage } from "../../utils/browserStorage";
import { sessionKey } from "../../Constants/appConstants";
import CreateEditRoleSidePanel from "./CreateEditRoleAPISidePanel";
import {
  deleteApiRelation,
  saveApiRelationsPayload,
  selectCurrentRoleId,
} from "../../store/slices/CreateRoleSlice";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import RoleSetupPanleComponent from "../CreateEditRoleCommonFolder/RoleSetupPanelComponent";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { triggerToast } from "../../utils/toast";
import { incrementTableRefresh } from "../../store/slices/apiDetailsSlice";
import GridTableCommon from "../GridTableCommon/GridTableCommon";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { getDataForTable } from "../../services/RequestAccess/RequestAccess";
import { formatFilterValue } from "../../utils/formatFilters";
import * as style from "./CreateEditRoleSidePanel.module.scss";
import { useIdToken } from "../../hooks/useAuthSession";

interface CreateEditRoleAPIProps {
  isEditable?: boolean;
}

const CreateEditRoleAPI: React.FC<CreateEditRoleAPIProps> = ({
  isEditable,
}) => {
  const scssClass = style as any;
  const idToken = useIdToken();
  const [openEditPanel, setOpenEditPanel] = useState(false);
  const [clickedRowId, setClickedRowId] = useState<number>();
  const { data } = useSelector((state: RootState) => state?.applicationDetails);
  const currentRoleId = useSelector(selectCurrentRoleId);
  const dispatch = useDispatch<AppDispatch>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [gridData, setGridData] = useState<GridTableDataType | any>({
    rows: [],
    totalCount: 0,
  });

  const tableRefresh = useSelector(
    (state: RootState) => state?.apiDetails?.tableRefresh,
  );

  const editPayload = useSelector(
    (state: RootState) => state.createRole.editApiRelationsPayload,
  );
  const saveApiPayload = useSelector(
    (state: RootState) => state.createRole.saveApiRelationsPayload,
  );

  const handleClose = () => {
    setOpenEditPanel(false);
  };

  const handleDeleteApiRelation = async (rows: any) => {
    try {
      const ids = rows?.map((row: any) => row?.toString());
      const response = await dispatch(deleteApiRelation(ids)).unwrap();
      if (response?.error) {
        triggerToast(`${response.error}`, "error");
      } else if (response?.status === true) {
        dispatch(incrementTableRefresh());
        triggerToast(`${response?.message}`, "success");
        setSelectedRows([]);
      }
    } catch (error: any) {
      // console.error("Error deleting API relation(s):", error);
      triggerToast("Error deleting API relation(s)", "error");
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    const resp = await getDataForTable(
      `/datasetup/api-details?appId=${data?.id}&roleId=${currentRoleId}`,
      {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? (filters || [])?.map((item: any) =>
                  formatFilterValue(item?.value, item?.id),
                )
              : [],
          sort: sorting?.map((item: any) => {
            return { columnName: item?.id, type: item?.desc ? "DESC" : "ASC" };
          }),
          page: {
            limit: pagination?.rowsPerPage,
            offset: pagination?.page,
          },
        },
      },
    );
    if (JSON.stringify(resp?.data) !== JSON.stringify(gridData)) {
      setGridData(resp?.data);
    }
    setIsLoading(false);
  };

  const checkIsDisable = () => {
    if (
      editPayload?.apiName &&
      editPayload?.endpointPath &&
      editPayload?.description &&
      editPayload?.methodType &&
      editPayload?.access &&
      (editPayload?.apiName !== saveApiPayload?.api_name ||
        editPayload?.endpointPath !== saveApiPayload?.endpoint_path ||
        editPayload?.description !== saveApiPayload?.description ||
        editPayload?.methodType !== saveApiPayload?.method_type ||
        editPayload?.access !== saveApiPayload?.access)
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    fetchData();
  }, [sorting, pagination, tableRefresh]);
  useEffect(() => {
    setPagination({
      page: 0,
      rowsPerPage: pagination?.rowsPerPage,
    });
  }, [filters]);

  return (
    <>
      <div className={scssClass.tableContainer}>
        {idToken && (
          <GridTableCommon
            gridData={gridData ?? {}}
            selectedRows={selectedRows}
            refresh={tableRefresh}
            onSelectionChange={setSelectedRows}
            style={{
              height: "calc(100vh - 38rem)",
              maxWidth: "calc(100vw - 4rem)",
            }}
            keyId="id"
            title="API Details"
            loading={isLoading}
            filters={filters}
            sorting={sorting}
            setSorting={setSorting}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            rowActions={[
              {
                event: "Delete",
                callback: (_event: any, rows: any) => {
                  handleDeleteApiRelation(selectedRows);
                },
              },
            ]}
            cellActions={[
              {
                event: "Edit",
                disabled: !isEditable,
                callback: (_event: any, row: any) => {
                  setOpenEditPanel(true);
                  setClickedRowId(row?.row?.original?.id);
                  dispatch(saveApiRelationsPayload(row?.row?.original));
                },
                icon: true,
              },
            ]}
          />
        )}
      </div>

      {openEditPanel && (
        <RoleSetupPanleComponent
          open={openEditPanel}
          title="Edit API Mapping"
          isEdit={true}
          onClose={handleClose}
          isSubmitDisable={checkIsDisable()}
          BodyComponent={
            <CreateEditRoleSidePanel
              tableData={gridData?.rows ?? []}
              isEdit={true}
              clickedRowId={clickedRowId}
            />
          }
          tabValue={2}
        />
      )}
    </>
  );
};

export default CreateEditRoleAPI;
